/**
 * The shape of the road.
 *
 * Rather than fit a curve through the coins, the map fixes a gentle meander
 * down the page and places the coins on it, so the road has proper bends
 * regardless of how many coins a week holds and every coin sits on the
 * road's tangent. The meander is a slow sine with a faint second wave laid
 * over it, which keeps the bends from repeating too regularly. It is a
 * function of the canvas y, so it continues across week boundaries. Where
 * no road is drawn (between one land's last coin and the next land's banner)
 * the wave's phase is frozen, so the next land's road resumes at the x and
 * heading where the previous one stopped and the two read as one road.
 *
 * Coins an editor has moved become anchors: the road's sideways offset from
 * the meander is interpolated smoothly from coin to coin, so it detours
 * through every moved coin without wandering back in between.
 */
import {splineSegments, type SplinePoint} from './pathSpline';

/** A span of canvas y with no road, across which the wave does not advance. */
export interface MeanderSkip {
  /** Top of the span, canvas y. */
  from: number;
  /** Bottom of the span, canvas y; must be greater than `from`. */
  to: number;
}

/** What a meander needs to know about the canvas. */
export interface Meander {
  /** The line the road wanders around. */
  centerX: number;
  /** How far the road swings to either side, in pixels. */
  amplitude: number;
  /** Spans where the phase is frozen. Defaults to none. */
  skips?: readonly MeanderSkip[];
}

/**
 * A coin's height and how far the road is pulled sideways there. Unmoved
 * coins are anchors too, with no pull, so the offset returns to zero at
 * them rather than lingering.
 */
export interface RoadAnchor {
  /** Canvas y of the anchor. */
  y: number;
  /** How far the road is pulled sideways at the anchor, in pixels. */
  dx: number;
  /**
   * Beyond the first and last anchors, over how many pixels the pull fades
   * to nothing.
   */
  reach: number;
}

/** Height of one full swing left and right, in pixels. */
const WAVELENGTH = 560;
/** The second wave's share of the amplitude and its relative frequency. */
const RIPPLE = 0.16;
const RIPPLE_FREQUENCY = 2.3;
/** Spacing of the samples the road is smoothed through, in pixels. */
const SAMPLE_STEP = 18;

/**
 * How far along the road a height is: the canvas y less every skipped span
 * above it, so the wave does not advance where no road is drawn.
 *
 * @param y - Canvas y in pixels.
 * @param meander - The meander, with its skips.
 * @return The distance the wave has travelled by that height.
 */
function roadDistance(y: number, meander: Meander): number {
  let distance = y;
  for (const skip of meander.skips ?? []) {
    if (y > skip.from) {
      distance -= Math.min(y, skip.to) - skip.from;
    }
  }
  return distance;
}

/**
 * Where the undisturbed meander is at a given height.
 *
 * @param y - Canvas y in pixels.
 * @param meander - The meander's centre, amplitude and skips.
 * @return The meander's x at that height.
 */
export function meanderX(y: number, meander: Meander): number {
  const t = (roadDistance(y, meander) / WAVELENGTH) * Math.PI * 2;
  return (
    meander.centerX +
    meander.amplitude *
      (Math.sin(t + 0.9) + RIPPLE * Math.sin(t * RIPPLE_FREQUENCY + 2.1))
  );
}

/**
 * Smooth ease between 0 and 1.
 *
 * @param t - Progress, clamped to [0, 1].
 * @return The eased progress.
 */
function ease(t: number): number {
  const clamped = Math.min(1, Math.max(0, t));
  return 0.5 * (1 - Math.cos(Math.PI * clamped));
}

/**
 * The road's sideways offset from the meander at a height.
 *
 * Between two anchors the offset eases from one's pull to the next's.
 * Outside the first and last anchors it eases back to zero over the
 * anchor's reach.
 *
 * @param y - Canvas y in pixels.
 * @param anchors - Anchors sorted by y. Empty means no offset.
 * @return The offset in pixels.
 */
function offsetAt(y: number, anchors: readonly RoadAnchor[]): number {
  if (anchors.length === 0) {
    return 0;
  }
  const first = anchors[0];
  const last = anchors[anchors.length - 1];
  if (y <= first.y) {
    return first.dx * (1 - ease((first.y - y) / Math.max(1, first.reach)));
  }
  if (y >= last.y) {
    return last.dx * (1 - ease((y - last.y) / Math.max(1, last.reach)));
  }
  for (let i = 0; i + 1 < anchors.length; i += 1) {
    const a = anchors[i];
    const b = anchors[i + 1];
    if (y >= a.y && y <= b.y) {
      const t = ease((y - a.y) / Math.max(1, b.y - a.y));
      return a.dx + (b.dx - a.dx) * t;
    }
  }
  return 0;
}

/**
 * Where the road is at a given height, anchors included.
 *
 * @param y - Canvas y in pixels.
 * @param meander - The meander's centre and amplitude.
 * @param anchors - Anchors sorted by y. Empty means the bare meander.
 * @return The road's centre x at that height.
 */
export function roadX(
  y: number,
  meander: Meander,
  anchors: readonly RoadAnchor[] = [],
): number {
  return meanderX(y, meander) + offsetAt(y, anchors);
}

/**
 * The road between two heights, as one smooth SVG path.
 *
 * @param from - Starting canvas y.
 * @param to - Ending canvas y; must be greater than `from`.
 * @param meander - The meander's centre and amplitude.
 * @param anchors - Anchors sorted by y. Defaults to none.
 * @return An SVG path string, or an empty string when `to` is not below
 *     `from`.
 */
export function meanderPath(
  from: number,
  to: number,
  meander: Meander,
  anchors: readonly RoadAnchor[] = [],
): string {
  if (to <= from) {
    return '';
  }
  const samples: SplinePoint[] = [];
  for (let y = from; y < to; y += SAMPLE_STEP) {
    samples.push({x: roadX(y, meander, anchors), y});
  }
  samples.push({x: roadX(to, meander, anchors), y: to});
  return splineSegments(samples).join(' ');
}
